import { type UseMutationResult } from '@tanstack/react-query';

import { useApiMutation } from '../../../lib/api/hooks.js';
import { parseOrThrow } from '../../../lib/api/contract.js';
import { queryKeys } from '../../../lib/api/query-keys.js';
import { ratePlanEndpoints } from './endpoints.js';
import { createdRatePlan, type RatePlanFormInput, type CreatedRatePlan } from './schemas.js';

const WRITTEN_KEYS = [queryKeys.ratePlans.lists, queryKeys.ratePlans.details];

function optionalNumber(value: string): number | undefined | null {
  if (value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function optionalStringDate(value: string): string | undefined | null {
  return value === '' ? null : value;
}

function optionalId(value: string): string | undefined | null {
  return value === '' ? null : value;
}

function optionalDescription(value: string): string | undefined | null {
  return value === '' ? null : value;
}

export function createRatePlanPayload(values: RatePlanFormInput): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    propertyId: values.propertyId,
    code: values.code.trim().toUpperCase(),
    name: values.name.trim(),
    type: values.type,
    currency: values.currency.trim().toUpperCase(),
    mealPlan: values.mealPlan,
    refundable: values.refundable,
    status: values.status,
    roomTypeIds: values.roomTypeIds,
  };

  const description = optionalDescription(values.description);
  if (description !== undefined) payload.description = description;

  const min = optionalNumber(values.minLengthOfStay);
  if (min !== null) payload.minLengthOfStay = min;

  const max = optionalNumber(values.maxLengthOfStay);
  if (max !== null) payload.maxLengthOfStay = max;

  const advance = optionalNumber(values.advanceBookingDays);
  if (advance !== null) payload.advanceBookingDays = advance;

  const activeFrom = optionalStringDate(values.activeFrom);
  if (activeFrom !== null) payload.activeFrom = activeFrom;

  const activeTo = optionalStringDate(values.activeTo);
  if (activeTo !== null) payload.activeTo = activeTo;

  const cancellation = optionalId(values.cancellationPolicyId);
  if (cancellation !== null) payload.cancellationPolicyId = cancellation;

  const payment = optionalId(values.paymentPolicyId);
  if (payment !== null) payload.paymentPolicyId = payment;

  const pmsRateId = values.pmsRateId.trim();
  if (pmsRateId !== '') payload.pmsRateId = pmsRateId;

  return payload;
}

export function updateRatePlanPayload(
  values: RatePlanFormInput,
  version: number,
): Record<string, unknown> {
  return {
    name: values.name.trim(),
    type: values.type,
    description: optionalDescription(values.description),
    currency: values.currency.trim().toUpperCase(),
    mealPlan: values.mealPlan,
    refundable: values.refundable,
    status: values.status,
    roomTypeIds: values.roomTypeIds,
    minLengthOfStay: optionalNumber(values.minLengthOfStay),
    maxLengthOfStay: optionalNumber(values.maxLengthOfStay),
    advanceBookingDays: optionalNumber(values.advanceBookingDays),
    activeFrom: optionalStringDate(values.activeFrom),
    activeTo: optionalStringDate(values.activeTo),
    cancellationPolicyId: optionalId(values.cancellationPolicyId),
    paymentPolicyId: optionalId(values.paymentPolicyId),
    pmsRateId: values.pmsRateId.trim() === '' ? null : values.pmsRateId.trim(),
    version,
  };
}

export function useCreateRatePlan(): UseMutationResult<
  CreatedRatePlan,
  unknown,
  RatePlanFormInput
> {
  return useApiMutation<RatePlanFormInput, CreatedRatePlan>({
    mutationFn: async (api, input) => {
      const payload = await api.post<unknown>(
        ratePlanEndpoints.create,
        createRatePlanPayload(input),
        crypto.randomUUID(),
      );
      return parseOrThrow(createdRatePlan, payload, ratePlanEndpoints.create);
    },
    invalidate: WRITTEN_KEYS,
  });
}

export interface UpdateRatePlanVariables {
  readonly ratePlanId: string;
  readonly version: number;
  readonly values: RatePlanFormInput;
}

export function useUpdateRatePlan(): UseMutationResult<unknown, unknown, UpdateRatePlanVariables> {
  return useApiMutation<UpdateRatePlanVariables, unknown>({
    mutationFn: async (api, { ratePlanId, version, values }) => {
      await api.patch<unknown>(
        ratePlanEndpoints.update(ratePlanId),
        updateRatePlanPayload(values, version),
      );
    },
    invalidate: WRITTEN_KEYS,
  });
}

export function useSoftDeleteRatePlan(): UseMutationResult<unknown, unknown, string> {
  return useApiMutation<string, unknown>({
    mutationFn: async (api, ratePlanId) => {
      await api.delete<unknown>(ratePlanEndpoints.delete(ratePlanId));
    },
    invalidate: WRITTEN_KEYS,
  });
}
